package macha.sh.kotlin

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import org.jetbrains.compose.web.css.CSSLengthValue
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/**
 * Central location that defines the typography tokens used across the site.
 *
 * Tweaking font families or sizes here will propagate everywhere the matching
 * token is used, keeping typography changes consistent and low effort.
 */
object SiteTypography {
    private val fallback = listOf("Helvetica Neue", "Arial", "sans-serif")
    val Body = FontSpec(
        family = listOf("Rajdhani-Light") + fallback,
        size = { it.fontSize(26.px) },
        lineHeight = 1.5)
    val Heading = FontSpec(
        family = listOf("Bungee Hairline") + fallback,
        size = { it.fontSize(3.cssRem) },
        lineHeight = 1.2)
    val Subheading = FontSpec(
        family = listOf("Orbitron") + fallback,
        size = { it.fontSize(1.cssRem) },
        lineHeight = 1.5)
    val Banner = FontSpec(
        family = listOf("BBHSansHegarty") + fallback,
        size = { it.fontSize(7.5.cssRem) })

    val Orbitron = FontSpec(
        family = listOf("Orbitron") + fallback,
        size = { it.fontSize(1.cssRem) },
        lineHeight = 1.5)
    val Bungee = FontSpec(
        family = listOf("Bungee Hairline") + fallback,
        size = { it.fontSize(3.cssRem) },
        lineHeight = 1.2)
    val BodyS = FontSpec(
        family = listOf("Rajdhani-Light") + fallback,
        size = { it.fontSize(1.cssRem) },
        lineHeight = 1.5)
    val BodyM = FontSpec(
        family = listOf("Rajdhani-Regular") + fallback,
        size = { it.fontSize(1.cssRem) },
        lineHeight = 1.5)
    val BodyL = FontSpec(
        family = listOf("Rajdhani-Bold") + fallback,
        size = { it.fontSize(1.cssRem) },
        lineHeight = 1.5)
}

data class FontSpec(
    val family: List<String>,
    val size: (Modifier) -> Modifier = { it },
    val lineHeight: Double? = null
)

fun Modifier.applyFont(
    spec: FontSpec,
    fontSize: CSSLengthValue? = null,
    lineHeight: Double? = spec.lineHeight
    ): Modifier {
        var result = this
        if (spec.family.isNotEmpty()) {
            val first = spec.family.first()
            val rest = spec.family.drop(1).toTypedArray()
            result = result.fontFamily(first, *rest)
        }
        result = if (fontSize != null) { result.fontSize(fontSize) } else { spec.size(result) }
        lineHeight?.let { result = result.lineHeight(it) }
        return result
}
